import * as React from 'react'
import styled, { css } from 'styled-components'
import theme from 'theme'
import Price from 'theme/molecules/Price'
import Title from './Title'
import FilterList from './FilterList'
import BuyWrapper from './BuyWrapper'
import BundlerButton from './BundlerButton'
import CustomizableButton from './CustomizableButton'
import calculateDeliveryDate from 'utils/calculateDeliveryDate'
import DeliveryInfo from 'theme/molecules/DeliveryInfo'
import DisposalOfLegacyDevice from './DisposalOfLegacyDevice'
import { useDisplayVariant } from 'modules/productDetail'
import { DeliveryInformation } from 'utils/calculateDeliveryDate'
import StockInfo from 'theme/molecules/StockInfo'
import CertifiedIcon from './CertifiedIcon'
import SizeTable from './SizeTable'
import CustomTailorProduct from './CustomTailorProduct/CustomTailorProduct'
import useCustomTailorProduct from './CustomTailorProduct/useCustomTailorProduct'

type Props = {
  brandPath: string
  onFilterDrawerClick: () => void
  onDeliveryInfoClick: () => void
  path: string
}

export default function BuyBox(props: Props) {
  const [amount, setAmount] = React.useState(1)
  const variant = useDisplayVariant()

  const customTailorContainerId = variant.data.related.standardProducts?.[0]
  const customTailorProduct = useCustomTailorProduct(
    variant.data.containerID,
    customTailorContainerId
  )

  const deliveryInformations: DeliveryInformation = React.useMemo(
    () => calculateDeliveryDate(variant.data, amount),
    [variant, amount]
  )

  return (
    <Wrapper
      className="BuyBox"
      $isFetching={!!variant.data.isDummy}
      data-cy-collection="BuyBox"
    >
      <Title brandPath={props.brandPath} />
      <Price
        prices={variant.data.prices}
        unit={variant.data.unit}
        logic="piece"
        options={{
          amount: amount,
          customTailor:
            variant.data.configurations.custom_tailor.is_custom_tailor,
          showdeliveryBadge: true,
          renderDeliveryBadgeIfNotAvailable: false
        }}
      />
      <FilterList openFilterDrawer={props.onFilterDrawerClick} />
      {variant.data.unit.purchaseUnit > 1 && (
        <Price
          data-cy-state="show-pack-price"
          prices={variant.data.prices}
          unit={variant.data.unit}
          logic="pack"
          options={{
            amount: amount,
            customTailor:
              variant.data.configurations.custom_tailor.is_custom_tailor
          }}
        />
      )}

      <BuyWrapper
        setAmount={setAmount}
        openFilterDrawer={props.onFilterDrawerClick}
        amount={amount}
        deliveryInformations={deliveryInformations}
      />
      <StockInfo stock={variant.data.stock} />
      <DeliveryInfo
        variant={variant.data}
        amount={amount}
        onPartnerItemClick={props.onDeliveryInfoClick}
        showPartnerInfo
      />
      <BundlerButton deliveryInformations={deliveryInformations} />
      <CustomizableButton path={props.path} />
      <SizeTable context="PDP" />
      {customTailorContainerId && customTailorProduct.showProduct && (
        <CustomTailorProduct
          data-cy-state="show-custom-tailor-product"
          title={customTailorProduct.product?.title || ''}
          brand={customTailorProduct?.product?.brand || ''}
          imageUrl={customTailorProduct?.product?.imageURL || ''}
          prices={customTailorProduct?.product?.prices || null}
          containerId={customTailorProduct?.product?.containerID || ''}
          sku={customTailorProduct?.product?.sku || ''}
          isLoading={customTailorProduct.isLoading}
        />
      )}
      <DisposalOfLegacyDevice />
      <CertifiedIcon />
    </Wrapper>
  )
}

const Wrapper = styled.div<{
  $isFetching: boolean
}>`
  ${(props) =>
    props.$isFetching &&
    css`
      * {
        color: transparent;
        text-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
      }
    `}
  > .Title {
    margin-bottom: ${theme.spacing('xs')};
  }
  > .rating-wrapper {
    margin-bottom: ${theme.spacing('s')};
  }
  > .Price {
    margin-bottom: ${theme.spacing('s')};
  }
  > .FilterList {
    margin-bottom: ${theme.spacing('s')};
  }
  > .BundlerButton {
    margin-bottom: ${theme.spacing('m')};
  }
  > .CustomizableButton {
    margin-bottom: ${theme.spacing('m')};
  }
  > .CustomTailorProduct {
    margin-top: ${theme.spacing('m')};
  }
  > .DisposalOfLegacyDevice {
    margin-top: ${theme.spacing('ml')};
  }
  > .CertifiedIcon {
    margin-top: ${theme.spacing('ml')};
  }
`
